import { useMutation, useQuery } from '@apollo/client';
import { GuideProgramItemInput } from '__generated__/globalTypes';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import {
    AddProgramItemToCategory,
    AddProgramItemToCategoryVariables
} from 'graphql/mutation/__generated__/AddProgramItemToCategory';
import {
    AssignRoomToGuideProgram,
    AssignRoomToGuideProgramVariables
} from 'graphql/mutation/__generated__/AssignRoomToGuideProgram';
import {
    CreateProgramItem,
    CreateProgramItemVariables
} from 'graphql/mutation/__generated__/createProgramItem';
import {
    LinkStoryToProgramItem,
    LinkStoryToProgramItemVariables
} from 'graphql/mutation/__generated__/linkStoryToProgramItem';
import {
    G_ADD_PROGRAM_ITEM_TO_CATEGORY,
    G_ASSIGN_ROOM_TO_PROGRAM,
    G_CREATE_PROGRAM_ITEM,
    G_LINK_STORY_TO_PROGRAM
} from 'graphql/mutation/guideMutation';
import {
    GuideQueryView,
    GuideQueryViewVariables
} from 'graphql/query/__generated__/GuideQueryView';
import { GUIDE_QUERY_VIEW } from 'graphql/query/guideQuery';
import { omit, uniqueId } from 'lodash';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

// Example
// {
//     date: '2024-02-21',
//     timeStart: '14:00',
//     timeEnd: '16:00',
//     title: 'Åpningsseremoni - dødsing og DJ Third Attempt',
//     description: '',
//     loadDetailFromStoryId: true | false // optional, alternative to providing title and description
//     roomId: '0.19668264626209386',
//     categoryIds: ['...'],
//     detailImportIds: [...],
// }
const ALL_DATA: Array<
    GuideProgramItemInput & { roomId?: string; categoryIds?: string[]; detailImportIds?: string[] }
> = [];
// const INDUSTRIUKA_GUIDE_ID = '3f2e5302-eaf3-4886-8346-1bc26225bd40';
const guideId = '';

export const ImportProgramPage = (props: Props): JSX.Element => {
    const importData = ALL_DATA;

    const guideQuery = useQuery<GuideQueryView, GuideQueryViewVariables>(GUIDE_QUERY_VIEW, {
        variables: {
            guideIdOrPath: guideId
        },
        nextFetchPolicy: 'network-only',
        skip: !guideId
    });

    useEffect(() => {
        guideQuery.refetch();
    }, [guideId]);

    const [createProgramMutation] = useMutation<CreateProgramItem, CreateProgramItemVariables>(
        G_CREATE_PROGRAM_ITEM
    );
    const [assignRoomMutation] = useMutation<
        AssignRoomToGuideProgram,
        AssignRoomToGuideProgramVariables
    >(G_ASSIGN_ROOM_TO_PROGRAM);

    const [addToCategoryMutation] = useMutation<
        AddProgramItemToCategory,
        AddProgramItemToCategoryVariables
    >(G_ADD_PROGRAM_ITEM_TO_CATEGORY);

    const [linkDetailMutation] = useMutation<
        LinkStoryToProgramItem,
        LinkStoryToProgramItemVariables
    >(G_LINK_STORY_TO_PROGRAM);

    const [responses, setResponses] = useState<string[]>([]);

    async function createPrograms() {
        for (const input of importData) {
            const resp = await createProgramMutation({
                variables: {
                    guideId,
                    input: omit(input, ['roomId', 'categoryIds', 'detailImportIds'])
                }
            });
            if (resp.errors && resp.errors?.length) {
                setResponses((prev) => [...prev, `ERROR: ${JSON.stringify(resp.errors)}`]);
                return;
            } else {
                setResponses((prev) => [
                    ...prev,
                    `OK: ${JSON.stringify(resp.data?.g_createProgramItem?.programItem)}`
                ]);
            }

            const guideProgramItemId =
                resp.data?.g_createProgramItem?.programItem?.guideProgramItemId;

            if (input.roomId) {
                const resp2 = await assignRoomMutation({
                    variables: {
                        guideId,
                        guideProgramItemId,
                        worldMapMarkerId: input.roomId
                    }
                });
                if (resp2.errors && resp2.errors?.length) {
                    setResponses((prev) => [...prev, `ERROR: ${JSON.stringify(resp2.errors)}`]);
                } else {
                    setResponses((prev) => [
                        ...prev,
                        `OK: ${JSON.stringify(resp2.data?.g_assignRoomToProgramItem?.programItem)}`
                    ]);
                }
            }

            if (input.categoryIds?.length) {
                for (const categoryId of input.categoryIds) {
                    await addToCategoryMutation({
                        variables: {
                            worldMapSetId: categoryId,
                            guideProgramItemId,
                            guideId
                        }
                    });
                }
            }

            if (input.detailImportIds?.length) {
                for (const detailImportId of input.detailImportIds) {
                    const storyId = guideQuery.data.guideView.stories.find(
                        (e) =>
                            e.importKey?.toString().toLowerCase() ===
                            detailImportId?.toString().toLowerCase()
                    )?.storyId;

                    if (storyId) {
                        await linkDetailMutation({
                            variables: {
                                guideProgramItemId,
                                guideId,
                                storyId
                            }
                        });
                    }
                }
            }
        }
    }

    return (
        <S_ImportPage className={props.className}>
            <ActionButton text="Import all" onClick={createPrograms} />

            {responses &&
                responses?.length > 0 &&
                responses.map((response, i) => (
                    <S_Response key={uniqueId()}>
                        {i}: {response}
                    </S_Response>
                ))}
        </S_ImportPage>
    );
};

const S_ImportPage = styled(S_PageStyle)`
    padding: 30px;
    padding-right: 550px;
`;

const S_Response = styled.div`
    margin-bottom: 20px;
`;
